import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  clearAnalyticsCookies,
  readStoredCookieConsent,
  saveCookieConsent,
  type CookieConsentPreferences,
  type StoredCookieConsent,
} from '../../lib/cookieConsent';
import {
  disableGoogleAnalytics,
  enableGoogleAnalytics,
  getGoogleAnalyticsMeasurementId,
  trackGoogleAnalyticsPageView,
} from '../../lib/analytics';

type CookieConsentContextValue = {
  consent: StoredCookieConsent | null;
  analyticsAllowed: boolean;
  openSettings: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function CookieToggle({
  enabled,
  onChange,
  disabled = false,
}: {
  enabled: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={enabled}
      aria-disabled={disabled}
      onClick={() => {
        if (!disabled && onChange) {
          onChange(!enabled);
        }
      }}
      className={`relative inline-flex h-8 w-14 items-center rounded-full border transition-all ${
        enabled
          ? 'border-brand-green/40 bg-brand-green/20'
          : 'border-white/10 bg-white/[0.05]'
      } ${disabled ? 'cursor-default opacity-90' : 'cursor-pointer hover:border-brand-green/30'}`}
    >
      <span
        className={`absolute left-1 top-1 inline-block h-6 w-6 rounded-full shadow-[0_4px_14px_rgba(0,0,0,0.28)] transition-transform ${
          enabled ? 'translate-x-6 bg-brand-green' : 'translate-x-0 bg-white/85'
        }`}
      />
    </button>
  );
}

function CookieDetailsCard({
  title,
  badge,
  badgeClassName,
  toggle,
  summary,
  details,
  expanded,
  onToggle,
  disabled = false,
}: {
  title: string;
  badge: string;
  badgeClassName: string;
  toggle: ReactNode;
  summary: string;
  details: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <span className={badgeClassName}>{badge}</span>
          </div>
          <p className="mt-3 text-sm leading-7 text-white/60">{summary}</p>
        </div>
        {toggle}
      </div>

      <button
        type="button"
        onClick={onToggle}
        className={`mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/18 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/62 transition-all hover:border-brand-green/25 hover:text-white ${disabled ? 'opacity-90' : ''}`}
        aria-expanded={expanded}
      >
        <i className={`text-sm transition-transform ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
        <span>{expanded ? t('cookies.modal.hideDetails') : t('cookies.modal.showDetails')}</span>
      </button>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="mt-5 space-y-4 rounded-[20px] border border-white/8 bg-black/20 p-4 sm:p-5">
              {details}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function CookieConsentProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);
  const [consent, setConsent] = useState<StoredCookieConsent | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(false);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const [necessaryExpanded, setNecessaryExpanded] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);

  useEffect(() => {
    const storedConsent = readStoredCookieConsent();
    setConsent(storedConsent);
    setDraftAnalytics(storedConsent?.analytics ?? false);
    setInitialized(true);

    if (storedConsent?.analytics) {
      enableGoogleAnalytics().then((ready) => {
        setAnalyticsReady(ready);
      });
    }
  }, []);

  useEffect(() => {
    if (settingsOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [settingsOpen]);

  useEffect(() => {
    if (!initialized || !consent?.analytics || !analyticsReady) {
      return;
    }

    trackGoogleAnalyticsPageView(
      `${location.pathname}${location.search}`,
      document.title,
    );
  }, [analyticsReady, consent?.analytics, initialized, location.pathname, location.search]);

  const openSettingsModal = () => {
    setDraftAnalytics(consent?.analytics ?? false);
    setNecessaryExpanded(false);
    setAnalyticsExpanded(false);
    setSettingsOpen(true);
  };

  const applyConsent = async (preferences: CookieConsentPreferences) => {
    const stored = saveCookieConsent(preferences);
    setConsent(stored);
    setDraftAnalytics(preferences.analytics);
    setSettingsOpen(false);

    if (preferences.analytics) {
      const ready = await enableGoogleAnalytics();
      setAnalyticsReady(ready);
      return;
    }

    disableGoogleAnalytics();
    clearAnalyticsCookies(getGoogleAnalyticsMeasurementId());
    setAnalyticsReady(false);
  };

  const contextValue = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      analyticsAllowed: !!consent?.analytics,
      openSettings: openSettingsModal,
    }),
    [consent],
  );

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}

      {initialized && !consent ? (
        <div className="fixed inset-x-0 bottom-0 z-[120] px-4 pb-4 pt-6 sm:px-6">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(13,13,13,0.96)_0%,rgba(20,20,20,0.92)_100%)] shadow-[0_24px_60px_rgba(0,0,0,0.45)] backdrop-blur-2xl">
            <div className="grid gap-6 px-5 py-5 sm:px-6 sm:py-6 lg:grid-cols-[minmax(0,1.2fr)_auto] lg:items-end">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-brand-green">
                  <i className="ri-shield-check-line text-sm" />
                  <span>{t('cookies.banner.badge')}</span>
                </div>
                <h2 className="text-xl font-semibold text-white sm:text-2xl">
                  {t('cookies.banner.title')}
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-7 text-white/68 sm:text-[15px]">
                  {t('cookies.banner.description')}
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <button
                  type="button"
                  onClick={openSettingsModal}
                  className="inline-flex min-w-[220px] items-center justify-center rounded-[18px] border border-white/10 bg-white/[0.05] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:border-brand-green/30 hover:bg-white/[0.08]"
                >
                  {t('cookies.banner.settings')}
                </button>
                <button
                  type="button"
                  onClick={() => applyConsent({ necessary: true, analytics: false })}
                  className="inline-flex min-w-[220px] items-center justify-center rounded-[18px] border border-white/10 bg-[#1A1A1A] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:border-white/12 hover:bg-[#202020]"
                >
                  {t('cookies.banner.rejectAll')}
                </button>
                <button
                  type="button"
                  onClick={() => applyConsent({ necessary: true, analytics: true })}
                  className="inline-flex min-w-[220px] items-center justify-center rounded-[18px] border border-brand-green/20 bg-gradient-to-r from-brand-green via-[#b4ec4b] to-[#c8f06d] px-5 py-3.5 text-sm font-bold text-[#0d0d0d] shadow-[0_16px_30px_rgba(163,230,53,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_34px_rgba(163,230,53,0.22)]"
                >
                  {t('cookies.banner.acceptAll')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {settingsOpen ? (
        <div className="fixed inset-0 z-[130] overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-6">
          <div className="flex min-h-full items-start justify-center sm:items-center">
            <div className="relative flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(13,13,13,0.98)_0%,rgba(20,20,20,0.94)_100%)] shadow-[0_28px_70px_rgba(0,0,0,0.48)] sm:max-h-[calc(100vh-3rem)]">
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                className="absolute right-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/72 transition-all hover:border-white/14 hover:bg-white/[0.08] hover:text-white"
                aria-label={t('cookies.modal.close')}
              >
                <i className="ri-close-line text-xl" />
              </button>

              <div className="overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
                <div className="max-w-2xl">
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-brand-green">
                    <i className="ri-settings-5-line text-sm" />
                    <span>{t('cookies.modal.badge')}</span>
                  </div>
                  <h2 className="text-2xl font-semibold text-white sm:text-3xl">
                    {t('cookies.modal.title')}
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-white/68 sm:text-[15px]">
                    {t('cookies.modal.description')}
                  </p>
                </div>

                <div className="mt-8 space-y-4">
                  <CookieDetailsCard
                    title={t('cookies.modal.necessaryTitle')}
                    badge={t('cookies.modal.alwaysOn')}
                    badgeClassName="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/58"
                    toggle={<CookieToggle enabled={true} disabled />}
                    summary={t('cookies.modal.necessarySummary')}
                    expanded={necessaryExpanded}
                    onToggle={() => setNecessaryExpanded((current) => !current)}
                    disabled
                    details={
                      <p className="text-sm leading-7 text-white/66">
                        {t('cookies.modal.necessaryDescription')}
                      </p>
                    }
                  />

                  <CookieDetailsCard
                    title={t('cookies.modal.analyticsTitle')}
                    badge={t('cookies.modal.optional')}
                    badgeClassName="rounded-full border border-brand-green/20 bg-brand-green/[0.08] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-green"
                    toggle={<CookieToggle enabled={draftAnalytics} onChange={setDraftAnalytics} />}
                    summary={t('cookies.modal.analyticsSummary')}
                    expanded={analyticsExpanded}
                    onToggle={() => setAnalyticsExpanded((current) => !current)}
                    details={
                      <>
                        <p className="text-sm leading-7 text-white/66">
                          {t('cookies.modal.analyticsDescription')}
                        </p>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-white/46">
                              {t('cookies.modal.analyticsProviderTitle')}
                            </div>
                            <p className="mt-2 text-sm leading-6 text-white/64">
                              {t('cookies.modal.analyticsProviderDescription')}
                            </p>
                          </div>
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-white/46">
                              {t('cookies.modal.analyticsDurationTitle')}
                            </div>
                            <p className="mt-2 text-sm leading-6 text-white/64">
                              {t('cookies.modal.analyticsDurationDescription')}
                            </p>
                          </div>
                        </div>
                      </>
                    }
                  />
                </div>

                <p className="mt-6 text-sm leading-7 text-white/52">
                  {t('cookies.modal.policyNote')}
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <button
                    type="button"
                    onClick={() => applyConsent({ necessary: true, analytics: false })}
                    className="inline-flex items-center justify-center rounded-[18px] border border-white/10 bg-[#1A1A1A] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:border-white/12 hover:bg-[#202020]"
                  >
                    {t('cookies.modal.rejectAll')}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyConsent({ necessary: true, analytics: draftAnalytics })}
                    className="inline-flex items-center justify-center rounded-[18px] border border-white/10 bg-white/[0.06] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:border-brand-green/30 hover:bg-white/[0.08]"
                  >
                    {t('cookies.modal.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyConsent({ necessary: true, analytics: true })}
                    className="inline-flex items-center justify-center rounded-[18px] border border-brand-green/20 bg-gradient-to-r from-brand-green via-[#b4ec4b] to-[#c8f06d] px-5 py-3.5 text-sm font-bold text-[#0d0d0d] shadow-[0_16px_30px_rgba(163,230,53,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_34px_rgba(163,230,53,0.22)]"
                  >
                    {t('cookies.modal.acceptAll')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);

  if (!context) {
    throw new Error('useCookieConsent must be used inside CookieConsentProvider');
  }

  return context;
}
